// What the Platforms, Leads and Lead finder pages share: the bucket and comparison
// badges, what a lead or candidate is on, and a page's weak-site signals as reasons.

import { Link } from 'react-router-dom'
import type { Bucket, LinkedPlatform, PriceVsScoreLm, SiteSignals, VsScoreLm } from '@/api/types'
import { Badge, Card, CardHeader } from '@/components/ui'
import { BUCKET_LABELS, BUCKET_TONE, PRICE_VS_LABELS, PRICE_VS_TONE, SCORELM_BASELINE, VS_SCORELM_LABELS, VS_SCORELM_TONE } from '@/lib/growth'

export function BucketBadge({ bucket }: { bucket: Bucket }) {
  return <Badge tone={BUCKET_TONE[bucket]}>{BUCKET_LABELS[bucket]}</Badge>
}

export function VsBadge({ value }: { value: VsScoreLm }) {
  return <Badge tone={VS_SCORELM_TONE[value]}>{VS_SCORELM_LABELS[value]}</Badge>
}

export function PriceVsBadge({ value }: { value: PriceVsScoreLm }) {
  return <Badge tone={PRICE_VS_TONE[value]}>{PRICE_VS_LABELS[value]}</Badge>
}

/** The linked platform, or the free text a lead still has, for a table cell. */
export function PlatformName({ linked, text }: { linked: Pick<LinkedPlatform, 'id' | 'name'> | null; text: string }) {
  if (linked) {
    return (
      <Link to={`/growth/platforms/${linked.id}`} className="underline decoration-line-strong underline-offset-2 hover:text-accent" onClick={(e) => e.stopPropagation()}>
        {linked.name}
      </Link>
    )
  }
  return <span className="text-muted">{text === 'unknown' ? 'Unknown' : text}</span>
}

/** Why, in the page's own terms. A weak signal says so. */
export function SiteSignalList({ site }: { site: SiteSignals | null }) {
  if (!site) return <p className="text-[13px] text-muted">No page has been read yet.</p>
  if (site.signals.length === 0) return <p className="text-[13px] text-muted">Nothing weak on the page we read.</p>
  return (
    <ul aria-label="What the site shows" className="grid gap-1 text-[13px] text-ink">
      {site.signals.map((s) => (
        <li key={s.key}>
          {s.label}
          {s.detail ? ` (${s.detail})` : ''}
          {s.weak ? <span className="text-muted"> · weak signal</span> : null}
        </li>
      ))}
    </ul>
  )
}

/** ScoreLM's own price, to read a platform's against. */
export function BaselineCard() {
  return (
    <Card>
      <CardHeader title="ScoreLM" description={SCORELM_BASELINE.terms} />
      <div className="grid grid-cols-3 gap-3">
        {SCORELM_BASELINE.plans.map((p) => (
          <div key={p.name} className="grid gap-0.5">
            <span className="text-xs font-semibold text-muted">{p.name}</span>
            <span className="font-mono text-[15px] font-semibold tabular text-ink">${p.monthly_usd}</span>
          </div>
        ))}
      </div>
    </Card>
  )
}
